import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';
import { isMobileDevice } from '../utils/device';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState(null);
  const [version, setVersion] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.getCompanySettings().then(res => setCompany(res.data)).catch(() => {});
    api.getAppVersion().then(res => {
      if (res.data.deploy_at) {
        const d = new Date(res.data.deploy_at);
        const dateStr = d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        setVersion(dateStr);
      }
    }).catch(() => {});
  }, []);

  const [showRoleChoice, setShowRoleChoice] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(username, password);
      // Se l'utente è su smartphone, reindirizza sempre all'interfaccia mobile
      if (isMobileDevice()) {
        navigate('/m');
        setLoading(false);
        return;
      }
      if (user.also_admin && user.role === 'teacher') {
        setShowRoleChoice(true);
        setLoading(false);
        return;
      }
      if (user.role === 'admin' || user.role === 'collaborator' || user.role === 'other') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotUsername.trim()) return;
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const res = await api.forgotPassword(forgotUsername.trim());
      setForgotMessage(res.data.message);
    } catch (err) {
      setForgotMessage(err.response?.data?.detail || 'Errore nella richiesta');
    } finally { setForgotLoading(false); }
  };

  return (
    <div className="login-container">
      {/* Company Info Side */}
      <div className="login-image-side bg-[#2D2A4A] flex items-center justify-center p-8">
        <div className="w-full max-w-[380px] text-center">
          {company?.logo_url ? (
            <img src={company.logo_url} alt={company?.nome || 'Logo'} className="w-full max-w-[260px] mx-auto mb-6" data-testid="login-company-logo" />
          ) : (
            <div className="w-48 h-48 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center" data-testid="login-company-logo-placeholder">
              <span className="text-white/40 text-5xl font-bold font-['Work_Sans']">{(company?.nome || 'FM').charAt(0)}</span>
            </div>
          )}
          {company && (
            <div className="text-white/80 space-y-1.5 text-sm" data-testid="login-company-info">
              <p className="text-white font-semibold text-lg mb-3">{company.nome}</p>
              {company.indirizzo && <p>{company.indirizzo}</p>}
              {(company.telefono || company.email) && (
                <p>
                  {company.telefono && <>Tel. {company.telefono}</>}
                  {company.telefono && company.email && ' - '}
                  {company.email && <>{company.email}</>}
                </p>
              )}
              {(company.piva || company.cf) && (
                <p>
                  {company.piva && <>P.IVA: {company.piva}</>}
                  {company.piva && company.cf && company.piva !== company.cf && <> C.F.: {company.cf}</>}
                </p>
              )}
              {company.pec && <p>PEC: {company.pec}</p>}
              {company.iban && <p className="font-mono text-xs text-white/60">IBAN: {company.iban}</p>}
              {company.segreteria && <p className="mt-2 text-white/60">Segreteria: {company.segreteria}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Form Side */}
      <div className="login-form-side">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo & Title */}
          <div className="mb-10">
            <p className="label-overline mb-2">{company?.nome || 'ACCADEMIA FIORI MUSICALI'}</p>
            <h1 className="text-4xl font-bold font-['Work_Sans'] tracking-tight mb-2">
              {forgotMode ? 'Password Dimenticata' : 'Bentornato'}
            </h1>
            <p className="text-[#52525B]">
              {forgotMode ? 'Inserisci il tuo username per ricevere il link di reset' : 'Accedi per gestire il tuo orario settimanale'}
            </p>
          </div>

          {forgotMode ? (
            <div className="space-y-6">
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-overline">Username</Label>
                  <Input value={forgotUsername} onChange={e => setForgotUsername(e.target.value)} placeholder="cognome nome" required className="h-12 rounded-sm" data-testid="forgot-username-input" />
                </div>
                {forgotMessage && (
                  <div className="p-4 bg-blue-50 border border-blue-200 text-blue-700 rounded-sm text-sm" data-testid="forgot-message">{forgotMessage}</div>
                )}
                <Button type="submit" disabled={forgotLoading} className="w-full h-12 bg-[#2D2A4A] rounded-sm text-base" data-testid="forgot-submit">
                  {forgotLoading ? 'Invio...' : 'Invia Link di Reset'}
                </Button>
              </form>
              <button onClick={() => { setForgotMode(false); setForgotMessage(''); }} className="w-full text-center text-sm text-[#52525B] hover:text-[#0A0A0A]" data-testid="back-to-login">Torna al Login</button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="label-overline">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="cognome nome"
                required
                data-testid="login-username-input"
                className="h-12 rounded-sm border-[#E4E4E7] focus:border-[#0A0A0A] focus:ring-[#0A0A0A]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="label-overline">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="h-12 rounded-sm border-[#E4E4E7] focus:border-[#0A0A0A] focus:ring-[#0A0A0A] pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#0A0A0A] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={() => { setForgotMode(true); setForgotMessage(''); setForgotUsername(username); }} className="text-xs text-[#52525B] hover:text-[#0A0A0A] transition-colors" data-testid="forgot-password-link">Password dimenticata?</button>
            </div>

            {error && (
              <div 
                className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-sm text-sm"
                data-testid="login-error-message"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-sm font-medium text-base transition-all duration-200"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">&#8987;</span>
                  Accesso in corso...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Accedi
                  <ArrowRight size={18} weight="bold" />
                </span>
              )}
            </Button>
          </form>
          )}

          {/* Role choice for director */}
          {showRoleChoice && (
            <div className="mt-6 space-y-3 animate-fade-in">
              <p className="text-center text-sm text-[#52525B] font-medium mb-4">Come vuoi accedere?</p>
              <button onClick={() => navigate('/admin')} className="w-full py-3 bg-[#2D2A4A] text-white font-semibold rounded-sm hover:bg-[#1e1c38] transition-colors text-sm" data-testid="role-choice-admin">
                Amministratore
              </button>
              <button onClick={() => navigate('/dashboard')} className="w-full py-3 bg-white border-2 border-[#2D2A4A] text-[#2D2A4A] font-semibold rounded-sm hover:bg-[#F4F4F5] transition-colors text-sm" data-testid="role-choice-teacher">
                Insegnante
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-[#52525B]">
            Contatta l'amministratore per le credenziali
          </p>
          {version && (
            <p className="mt-2 text-center text-xs text-[#52525B] font-medium" data-testid="app-version">
              v. {version}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
